
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Trash, Plus, Globe } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';

interface DomainManagementProps {
  settings: {
    domains: string[];
    primaryDomain: string;
    allowCustomDomain: boolean;
  };
  isSaving: boolean;
  addDomain: (domain: string) => Promise<boolean>;
  removeDomain: (domain: string) => Promise<boolean>;
  setPrimaryDomain: (domain: string) => Promise<boolean>;
  toggleCustomDomain: (allow: boolean) => Promise<boolean>;
}

const DomainManagement: React.FC<DomainManagementProps> = ({
  settings,
  isSaving,
  addDomain,
  removeDomain,
  setPrimaryDomain,
  toggleCustomDomain
}) => {
  const [newDomain, setNewDomain] = useState('');
  const { toast } = useToast();

  const handleAddDomain = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDomain) {
      toast({
        title: "Error",
        description: "Please enter a domain",
        variant: "destructive",
      });
      return;
    }
    
    // Basic domain validation
    const domainRegex = /^[a-zA-Z0-9][a-zA-Z0-9-]{1,61}[a-zA-Z0-9]\.[a-zA-Z]{2,}$/;
    if (!domainRegex.test(newDomain)) {
      toast({
        title: "Invalid Domain",
        description: "Please enter a valid domain name",
        variant: "destructive",
      });
      return;
    }
    
    const success = await addDomain(newDomain);
    if (success) {
      setNewDomain('');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Globe className="mr-2 h-5 w-5" />
          Domain Management
        </CardTitle>
        <CardDescription>
          Configure domain settings for this instance
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-4">
            <Label htmlFor="custom-domain">Allow Custom Domains</Label>
            <Switch 
              id="custom-domain" 
              checked={settings.allowCustomDomain}
              onCheckedChange={toggleCustomDomain}
              disabled={isSaving}
            />
          </div>
          
          <div className="text-sm text-gray-500 mb-4">
            {settings.allowCustomDomain 
              ? "This instance is allowed to use custom domains" 
              : "This instance can only use subdomains on the platform domain"}
          </div>
        </div>
        
        <Separator className="my-4" />
        
        <div className="space-y-4">
          <h3 className="text-sm font-medium">Current Domains</h3>
          
          <div className="space-y-2">
            {settings.domains.map((domain) => (
              <div key={domain} className="flex items-center justify-between p-3 border rounded-md bg-gray-50">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-mono">{domain}</span>
                  {domain === settings.primaryDomain && (
                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      Primary
                    </Badge>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  {domain !== settings.primaryDomain && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => setPrimaryDomain(domain)}
                      disabled={isSaving}
                    >
                      Set as Primary
                    </Button>
                  )}
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    onClick={() => removeDomain(domain)}
                    disabled={domain === settings.primaryDomain || isSaving}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          <form onSubmit={handleAddDomain} className="flex items-center space-x-2 mt-4">
            <Input
              placeholder="Enter new domain"
              value={newDomain}
              onChange={(e) => setNewDomain(e.target.value)}
              disabled={isSaving}
              className="flex-1"
            />
            <Button type="submit" disabled={isSaving}>
              <Plus className="h-4 w-4 mr-2" />
              Add Domain
            </Button>
          </form>
        </div>
      </CardContent>
    </Card>
  );
};

export default DomainManagement;
